import axios from 'axios';
import { API_BASE_URL, STORAGE_KEYS } from '../config/config';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true // Important for session cookies
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const user = localStorage.getItem(STORAGE_KEYS.USER);
    if (user) {
      const userData = JSON.parse(user);
      config.headers['X-User-ID'] = userData.id;
      if (userData.institute_id) {
        config.headers['X-Institute-ID'] = userData.institute_id;
      }
      if (userData.role) {
        config.headers['X-User-Role'] = userData.role;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Don't auto-logout on 401 - let components handle authentication
    // This allows user to stay logged in across page refreshes
    return Promise.reject(error);
  }
);

// Authentication API
export const authAPI = {
  register: (data) => api.post('/register', data),
  login: (credentials) => api.post('/login', credentials),
  staffLogin: (credentials) => api.post('/staff-login', credentials),
  logout: () => api.post('/logout'),
  getProfile: () => api.get('/profile'),
  updateProfile: (formData) => api.post('/profile/update', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getMyPermissions: () => api.get('/my-permissions')
};

// Dashboard API
export const dashboardAPI = {
  getDashboard: () => api.get('/dashboard')
};

// Course API
export const courseAPI = {
  getCourses: () => api.get('/courses'),
  getTeachers: () => api.get('/teachers'),
  createCourse: (formData) => api.post('/course/create', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  updateCourse: (id, formData) => api.post(`/course/${id}/update`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteCourse: (id) => api.delete(`/course/${id}`),
  toggleCourseStatus: (id) => api.post(`/course/${id}/toggle-status`),
  updateCoursePosition: (id, position) => api.post(`/course/${id}/update-position`, { position }),
  
  // Folder Management
  getFolderTree: (courseId) => api.get(`/course/${courseId}/folder-tree`),
  createFolder: (data) => api.post('/folder/create', data),
  renameFolder: (folderId, data) => api.put(`/folder/${folderId}/rename`, data),
  deleteFolder: (folderId) => api.delete(`/folder/${folderId}`),
  getFolderContents: (folderId) => api.get(`/folder/${folderId}/contents`),
  
  // Content Management (matching admin/addcontent)
  getAddContentData: (courseId, params) => api.get(`/course/${courseId}/addcontent`, { params }),
  getNestedFolders: (courseId) => api.get(`/course/${courseId}/folders-nested`),
  addVideo: (data) => api.post('/course/addvideo', data),
  updateVideo: (videoId, data) => api.post(`/course/updatevideo/${videoId}`, data),
  addDocument: (formData) => api.post('/course/adddocument', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  updateDocument: (docId, formData) => api.post(`/course/updatedocument/${docId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  
  // Folder Management (matching admin panel)
  createMainFolder: (data) => api.post('/createfolder', data), // admin/createfolder
  createSubfolder: (data) => api.post('/subfolder', data), // admin/subfolder
  
  // View Contents (matching admin/viewcontents)
  viewContents: (courseId) => api.get(`/viewcontents/${courseId}`),
  getDynamicFolder: (params) => api.get('/dynamic-folder', { params }),
  updateOrder: (data) => api.post('/update-order', data),
  removeExamFromFolder: (courseExamId) => api.delete(`/course-exam/${courseExamId}`),
  
  // Import Contents (matching admin/importcontents)
  getImportContents: (courseId) => api.get(`/importcontents/${courseId}`),
  getDynamicFolderImport: (params) => api.get('/dynamic-folder-import', { params }),
  importContents: (data) => api.post('/import', data),
  
  // Legacy Content Management
  uploadContent: (formData) => api.post('/content/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteContent: (contentId, type) => api.delete(`/content/${contentId}/${type}`),
  
  // Course Types (Categories)
  getCourseTypes: () => api.get('/master/course-types'),
  createCourseType: (data) => api.post('/master/course-type/create', data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  updateCourseType: (id, data) => api.post(`/master/course-type/${id}/update`, data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  toggleCourseTypeStatus: (id) => api.post(`/master/course-type/${id}/toggle-status`),
  deleteCourseType: (id) => api.delete(`/master/course-type/${id}`),
  updateCourseTypePosition: (id, position) => api.post(`/master/course-type/${id}/update-position`, { position }),
  
  // Course Preferences (Top-level categories)
  getCoursePreferences: () => api.get('/master/course-preferences'),
  
  // Allow Courses & Students
  getStudents: (params) => api.get('/students', { params }),
  getStudentById: (id) => api.get(`/student/${id}`),
  createStudent: (data) => api.post('/student/create', data),
  updateStudent: (id, data) => api.post(`/student/${id}/update`, data),
  getGeneralSettings: () => api.get('/general-settings'),
  allowCourse: (data) => api.post('/course/allow', data),
  
  // Allow Courses Online (matching admin panel)
  getAllowCoursesTradegroups: () => api.get('/allow-courses/tradegroups'),
  getAllowCoursesTrades: (tradegroupId) => api.get(`/allow-courses/trades?tradegroup_id=${tradegroupId}`),
  searchAllowCoursesStudents: (search, tradegroupId = null, tradeId = null, limit = 20) => {
    let url = `/allow-courses/students-search?search=${encodeURIComponent(search)}&limit=${limit}`;
    if (tradegroupId) url += `&tradegroup_id=${tradegroupId}`;
    if (tradeId) url += `&trade_id=${tradeId}`;
    return api.get(url);
  },
  searchStudentCourses: (studentId) => api.get(`/allow-courses/search-courses?student_id=${studentId}`),
  unpaidCourse: (courseId, email) => api.delete('/allow-courses/unpaid', { 
    data: { course_id: courseId, email: email } 
  }),
  
  // Live Videos
  getLiveVideos: () => api.get('/live-videos'),
  deleteLiveVideo: (id) => api.delete(`/live-video/${id}`)
};

// Teacher API
export const teacherAPI = {
  getTeachers: (params) => api.get('/teachers', { params }),
  getTeacher: (id) => api.get(`/teacher/${id}`),
  getFormData: () => api.get('/teachers/form-data'),
  addTeacher: (data) => api.post('/teacher/create', data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  updateTeacher: (id, data) => api.post(`/teacher/${id}/update`, data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteTeacher: (id) => api.delete(`/teacher/${id}`),
  getMenuStructure: () => api.get('/menus'),
  getPermissions: (teacherId) => api.get(`/teacher/${teacherId}/permissions`),
  updatePermissions: (teacherId, permissions) => api.post(`/teacher/${teacherId}/permissions`, { permissions }),
  copyRolePermissions: (teacherId) => api.post(`/teacher/${teacherId}/copy-role-permissions`),
  getRolePermissions: (roleId) => api.get(`/role/${roleId}/permissions`),
  updateRolePermissions: (roleId, permissions) => api.post(`/role/${roleId}/permissions`, { permissions }),
  assignCourses: (teacherId, courseIds) => api.post(`/teacher/${teacherId}/assign-courses`, { course_ids: courseIds })
};

// Exam API
export const examAPI = {
  getExams: (params) => api.get('/exams', { params }),
  getExam: (id) => api.get(`/exam/${id}`),
  createExam: (data) => api.post('/exam/create', data),
  updateExam: (id, data) => api.post(`/exam/${id}/update`, data),
  deleteExam: (id) => api.delete(`/exam/${id}`),
  toggleStatus: (id) => api.post(`/exam/${id}/toggle-status`),
  getExamQuestions: (examId) => api.get(`/exam/${examId}/questions`),
  addQuestionsToExam: (examId, questionIds) => {
    const formData = new FormData();
    formData.append('examid', examId);
    questionIds.forEach(id => {
      formData.append('checkboxid[]', id);
    });
    return api.post(`/exam/${examId}/add-questions`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  
  // Question Bank
  getQuestionBank: (params) => api.get('/questions', { params }),
  getQuestion: (id) => api.get(`/question/${id}`),
  createQuestion: (data) => {
    // Check if it's a FormData object (for import)
    if (data instanceof FormData) {
      return api.post('/question/create', data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    }
    return api.post('/question/create', data);
  },
  updateQuestion: (id, data) => api.post(`/question/${id}/update`, data),
  deleteQuestion: (id) => api.delete(`/question/${id}`),
  bulkDeleteQuestions: (questionIds) => api.post('/questions/bulk-delete', { question_ids: questionIds }),
  getSampleWordFile: () => api.get('/sample-questions-word', { responseType: 'blob' }),
  
  // Assignment
  assignToCourse: (data) => api.post('/exam/assign-to-course', data)
};

// Master Data API
export const masterAPI = {
  // Trade Groups
  getTradeGroups: () => api.get('/master/tradegroups'),
  createTradeGroup: (data) => api.post('/master/tradegroup/create', data),
  updateTradeGroup: (id, data) => api.post(`/master/tradegroup/${id}/update`, data),
  disableTradeGroup: (id) => api.post(`/master/tradegroup/${id}/disable`),
  
  // Trades
  getTrades: () => api.get('/master/trades'),
  getTradesByGroup: (groupId) => api.get(`/master/trades-by-group?groupId=${groupId}`),
  createTrade: (data) => api.post('/master/trade/create', data),
  updateTrade: (id, data) => api.post(`/master/trade/${id}/update`, data),
  disableTrade: (id) => api.post(`/master/trade/${id}/disable`),
  
  // Subjects
  getSubjects: () => api.get('/master/subjects'),
  getSubjectsByTrade: (tradegroup, trade) => api.get(`/master/subjects-by-trade?tradegroup=${tradegroup}&trade=${trade}`),
  createSubject: (data) => api.post('/master/subject/create', data),
  updateSubject: (id, data) => api.post(`/master/subject/${id}/update`, data),
  disableSubject: (id) => api.post(`/master/subject/${id}/disable`),
  
  // Chapters
  getChapters: () => api.get('/master/chapters'),
  getChaptersBySubject: (tradegroup, trade, subject) => api.get(`/master/chapters-by-subject?tradegroup=${tradegroup}&trade=${trade}&subject=${subject}`),
  createChapter: (data) => api.post('/master/chapter/create', data),
  updateChapter: (id, data) => api.post(`/master/chapter/${id}/update`, data),
  disableChapter: (id) => api.post(`/master/chapter/${id}/disable`),
  
  // Topics
  getTopics: () => api.get('/master/topics'),
  getTopicsByChapter: (tradegroup, trade, subject, chapter) => api.get(`/master/topics-by-chapter?tradegroup=${tradegroup}&trade=${trade}&subject=${subject}&chapter=${chapter}`),
  createTopic: (data) => api.post('/master/topic/create', data),
  updateTopic: (id, data) => api.post(`/master/topic/${id}/update`, data),
  disableTopic: (id) => api.post(`/master/topic/${id}/disable`),
  
  // Classes & Batches
  getClasses: () => api.get('/master/classes'),
  createClass: (data) => api.post('/master/class/create', data),
  updateClass: (id, data) => api.post(`/master/class/${id}/update`, data),
  deleteClass: (id) => api.delete(`/master/class/${id}`),
  
  getBatches: () => api.get('/master/batches'),
  createBatch: (data) => api.post('/master/batch/create', data),
  updateBatch: (id, data) => api.post(`/master/batch/${id}/update`, data),
  deleteBatch: (id) => api.delete(`/master/batch/${id}`),
  
  // Staff Roles
  getStaffRoles: () => api.get('/staff/roles'),
  getStaffByRole: (params) => api.get('/staff/by-role', { params })
};

// AI API (Institute-specific with institute_id isolation)
export const aiAPI = {
  generateExam: (data) => api.post('/ai/generate-exam', data),
  saveQuestions: (data) => api.post('/ai/save-questions', data),
};

// Legal Pages API
export const legalPageAPI = {
  getPages: () => api.get('/legal-pages'),
  getPage: (id) => api.get(`/legal-page/${id}`),
  createPage: (formData) => api.post('/legal-page/create', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  updatePage: (id, formData) => api.post(`/legal-page/${id}/update`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deletePage: (id) => api.delete(`/legal-page/${id}`),
  toggleStatus: (id) => api.post(`/legal-page/${id}/toggle-status`)
};

// Payment API
export const paymentAPI = {
  getTransactions: (params = {}) => api.get('/transactions', { params }),
  getPaymentHistory: (params = {}) => api.get('/payment-history', { params }),
  getFailedPayments: (params = {}) => api.get('/failed-payments', { params })
};

// Fee Management API (Institute-specific)
export const feeAPI = {
  // Fee Master
  getFeeMasters: () => api.get('/fee/masters'),
  createFeeMaster: (data) => api.post('/fee/master/create', data),
  updateFeeMaster: (id, data) => api.post(`/fee/master/${id}/update`, data),
  disableFeeMaster: (id) => api.post(`/fee/master/${id}/disable`),
  
  // Fee Group
  getFeeGroups: () => api.get('/fee/groups'),
  createFeeGroup: (data) => api.post('/fee/group/create', data),
  updateFeeGroup: (id, data) => api.post(`/fee/group/${id}/update`, data),
  disableFeeGroup: (id) => api.post(`/fee/group/${id}/disable`),
  
  // Fee Type
  getFeeTypes: () => api.get('/fee/types'),
  createFeeType: (data) => api.post('/fee/type/create', data),
  updateFeeType: (id, data) => api.post(`/fee/type/${id}/update`, data),
  disableFeeType: (id) => api.post(`/fee/type/${id}/disable`),
  
  // Fee Discount
  getFeeDiscounts: () => api.get('/fee/discounts'),
  createFeeDiscount: (data) => api.post('/fee/discount/create', data),
  updateFeeDiscount: (id, data) => api.post(`/fee/discount/${id}/update`, data),
  disableFeeDiscount: (id) => api.post(`/fee/discount/${id}/disable`),
  
  // Fee reminders (dues + email / SMS / WhatsApp)
  getReminderSettings: () => api.get('/fee/reminder-settings'),
  updateReminderSettings: (data) => api.post('/fee/reminder-settings', data),
  getStudentDues: (params) => api.get('/fee/student-dues', { params }),
  createStudentDue: (data) => api.post('/fee/student-due/create', data),
  updateStudentDue: (id, data) => api.post(`/fee/student-due/${id}/update`, data),
  deleteStudentDue: (id) => api.delete(`/fee/student-due/${id}`),
  getReminderLogs: (params) => api.get('/fee/reminder-logs', { params }),
  runRemindersNow: (data) => api.post('/fee/reminders/run-now', data || {}),
  getReminderDiagnostics: () => api.get('/fee/reminder-diagnostics')
};

// Staff Attendance API
export const attendanceAPI = {
  getRoles: () => api.get('/attendance/roles'),
  getStaffByRole: (data) => api.post('/attendance/staff', data),
  saveAttendance: (data) => api.post('/attendance/save', data)
};

// Payroll API
export const payrollAPI = {
  getRoles: () => api.get('/payroll/roles'),
  getStaffList: (data) => api.post('/payroll/staff-list', data),
  getStaffDetails: (staffId) => api.get(`/payroll/staff/${staffId}`),
  savePayslip: (data) => api.post('/payroll/save', data),
  processPayment: (data) => api.post('/payroll/payment', data),
  revertPayslip: (payslipId) => api.post(`/payroll/revert/${payslipId}`),
  getPayslip: (payslipId) => api.get(`/payroll/payslip/${payslipId}`)
};

// Leave Request API
export const leaveRequestAPI = {
  getLeaveRequests: () => api.get('/leave-request/list'),
  getLeaveBalance: () => api.get('/leave-request/balance'),
  applyLeave: (data) => api.post('/leave-request/apply', data, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  }),
  deleteLeaveRequest: (id) => api.delete(`/leave-request/${id}`)
};

// Password Management API
export const passwordAPI = {
  changeStaffPassword: (data) => api.post('/staff/change-password', data),
  resetStudentPassword: (studentId, data) => api.post(`/students/${studentId}/reset-password`, data)
};

// Student Attendance API (for offline institutes)
export const studentAttendanceAPI = {
  getStudentsForAttendance: (params) => api.get('/attendance/students', { params }),
  markAttendance: (data) => api.post('/attendance/mark', data),
  scanAttendance: (data) => api.post('/attendance/scan', data),
  getAttendanceReport: (params) => api.get('/attendance/report', { params }),
  getStudentAttendanceSummary: (params) => api.get('/attendance/summary', { params }),
  deleteAttendance: (id) => api.delete(`/attendance/${id}`)
};

// Holiday API
export const holidayAPI = {
  getHolidays: (params) => api.get('/holidays', { params }),
  createHoliday: (data) => api.post('/holiday/create', data),
  updateHoliday: (id, data) => api.post(`/holiday/${id}/update`, data),
  deleteHoliday: (id) => api.delete(`/holiday/${id}`),
  checkHoliday: (params) => api.get('/holiday/check', { params })
};

// Staff Attendance API (QR Scanner)
export const staffAttendanceAPI = {
  scanAttendance: (data) => api.post('/staff/attendance/scan', data),
  getReport: (params) => api.get('/staff/attendance/report', { params }),
  getStaffForScanner: (params) => api.get('/staff/attendance/scanner', { params }),
  getRoles: () => api.get('/staff/roles'),
  getStaffByRole: (params) => api.get('/staff/by-role', { params })
};

export default api;

